import { SensorStatus } from "@sitescene/types";

import { BadgeColors } from "@/components/ui/badges/badge-types";

export type SensorDurationLabel = {
	color: string;
	text: string;
};

type SensorStatusConfig = {
	color: BadgeColors;
	label: string;
};
export const sensorStatusConfig = (
	status: SensorStatus,
): SensorStatusConfig => {
	switch (status) {
		case SensorStatus.Active:
			return {
				color: "success",
				label: "Online",
			};
		case SensorStatus.Offline:
			return {
				color: "error",
				label: "Offline",
			};
		case SensorStatus.Warning:
			return {
				color: "warning",
				label: "Warning",
			};
		default:
			return {
				color: "gray",
				label: "Unknown",
			};
	}
};

/**
 * Formats a millisecond duration as a human-readable string,
 * e.g. "2 days 4 hours 2 minutes", "4 hours 2 minutes", or "17 minutes".
 */
export const formatElapsedMs = (ms: number): string => {
	const totalMinutes = Math.floor(ms / (1000 * 60));
	const days = Math.floor(totalMinutes / (60 * 24));
	const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
	const minutes = totalMinutes % 60;

	const parts: string[] = [];
	if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
	if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
	if (minutes > 0 || parts.length === 0)
		parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);

	return parts.join(" ");
};

/**
 * Formats the elapsed time since a sensor's last input as a human-readable
 * duration string, e.g. "2 days 4 hours 2 minutes", "4 hours 2 minutes", or "17 minutes".
 *
 * Returns "" for invalid dates and for future timestamps (clock skew), so
 * callers never render misleading "0 minutes" labels from bad data.
 */
export const formatSensorElapsedDuration = (
	lastInputReceived: Date | string | undefined,
): string => {
	if (!lastInputReceived) return "";

	const last = new Date(lastInputReceived);
	const lastMs = last.getTime();
	if (Number.isNaN(lastMs)) return "";

	const diffMs = Date.now() - lastMs;
	if (diffMs < 0) return "";

	return formatElapsedMs(diffMs);
};

/**
 * Returns a status-specific duration label for a sensor, e.g.:
 * - Active:  { text: "Last responded at 6:45pm",     color: "text-utility-green-600" }
 * - Warning: { text: "Not responded for 28 minutes", color: "text-utility-yellow-600" }
 * - Offline: { text: "Offline for 4 hours 19 minutes", color: "text-utility-red-600" }
 */
export const getSensorDurationLabel = (
	status: SensorStatus,
	lastInputReceived: Date | null | string | undefined,
): null | SensorDurationLabel => {
	if (!lastInputReceived) return null;

	const parsedDate = new Date(lastInputReceived);
	if (Number.isNaN(parsedDate.getTime())) return null;

	const elapsedMs = Date.now() - parsedDate.getTime();
	if (elapsedMs < 0) return null;

	if (status === SensorStatus.Active) {
		const timeStr = parsedDate.toLocaleTimeString("en-US", {
			hour: "numeric",
			hour12: true,
			minute: "2-digit",
		});
		return {
			color: "text-utility-green-700",
			text: `Last responded at ${timeStr.toLowerCase()}`,
		};
	}

	const duration = formatElapsedMs(elapsedMs);

	if (status === SensorStatus.Warning) {
		return {
			color: "text-utility-yellow-600",
			text: `Not responded for ${duration}`,
		};
	}

	if (status === SensorStatus.Offline) {
		return {
			color: "text-utility-red-600",
			text: `Offline for ${duration}`,
		};
	}

	return null;
};
